import React, { useContext } from 'react';
import {
  BiData,
  BiContextProvidedValue,
  EditorButtonClickBiEvent,
} from '@wix/da-bi/pkg/events';
import IconButtonWithContextMenu, {
  IconButtonWithContextMenuProps,
} from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import { Props as IconWrapProps } from '@wix/da-ds/pkg/Icons/IconWrap';
import type { RequiresAccessibleLabel } from '@wix/da-a11y/pkg/typeHelpers';
import { TOOLTIP_HOVER_DELAY } from '../../../../constants';
import { EditorContext } from '../../../Editor/Context';
import ArrowIcon from './ArrowIcon';

import { popperDropdownConfig } from '../../../../extensions/extension-deviation/constants';
import s from './EditorIconButtonWithContextMenu.scss';

export type EditorContextMenuItem = NullableDropdownItem & {
  /** feature name for BI */
  featureId?: string;
  /** is this menu option currently active according to the editor api? */
  isActive?: boolean;
  /** the class/functional component of the icon to display (not a ReactNode) */
  IconComponent?: React.ComponentType<IconWrapProps>;
};
interface BaseProps extends Omit<
  IconButtonWithContextMenuProps,
  'size' | 'items'
> {
  items: EditorContextMenuItem[];
  featureId: string;
}

type Props = RequiresAccessibleLabel<BaseProps>;

const stdProps = {
  className: 'editor-sub-menu',
};

const EditorIconButtonWithContextMenu: React.FC<Props> = ({
  items,
  icon,
  featureId,
  ...props
}) => {
  const { ref: editor } = useContext(EditorContext);

  // show the current active icon as the menu icon
  // (NOTE! if there are multiple active marks, the -last- active in
  // the list will be shown as the menu's icon)...
  const lastActiveIcon =
    items.reduce<React.ComponentType<IconWrapProps> | null>(
      (acc, item) =>
        item?.isActive && item?.IconComponent ? item.IconComponent : acc,
      null
    ) ?? icon;
  // and, in the submenu, show any active marks as a special icon
  const transformedItems: NullableDropdownItem[] = items.map(item => {
    if (!item) {
      return null;
    }
    const {
      featureId: itemFeatureId,
      IconComponent,
      isActive,
      ...itemProps
    } = item;
    return {
      iconClassName: isActive ? s['active-icon'] : undefined,
      icon: IconComponent ? <IconComponent disableDefaultColors /> : undefined,
      biData: BiData<EditorButtonClickBiEvent>({
        evid: 193,
        view: BiContextProvidedValue,
        component: BiContextProvidedValue,
        itemid: BiContextProvidedValue,
        typeid: BiContextProvidedValue,
        preset: editor?.storage['da-presetId'] || 'unknown',
        feature: itemFeatureId || 'unknown',
      }),
      ...itemProps,
    } as NullableDropdownItem;
  });

  return (
    <div className={s['root']}>
      <IconButtonWithContextMenu
        icon={lastActiveIcon}
        size="large"
        tooltipHoverDelay={TOOLTIP_HOVER_DELAY}
        items={transformedItems}
        standardMenuProps={stdProps}
        popperManagerProps={popperDropdownConfig}
        biData={BiData<EditorButtonClickBiEvent>({
          evid: 193,
          view: BiContextProvidedValue,
          component: BiContextProvidedValue,
          itemid: BiContextProvidedValue,
          typeid: BiContextProvidedValue,
          preset: editor?.storage['da-presetId'] || 'unknown',
          feature: featureId,
        })}
        {...props}
      />
      <ArrowIcon className={s['icon']} />
    </div>
  );
};
EditorIconButtonWithContextMenu.displayName = 'EditorIconButtonWithContextMenu';

export default EditorIconButtonWithContextMenu;
